import type { Category, CategoryScore, Certainty, Finding, ScanResult, Severity } from '../types';
import { CATEGORY_HELP, findingHelpFor } from '../explanations';
import { engineName } from '../api';
import InfoDot from './InfoDot';

const CATEGORY_LABEL: Record<Category, string> = {
  crawlability: 'Crawlability (gate)',
  structure: 'Structure',
  entity: 'Entity',
  citability: 'Citability',
  offpage: 'Off-page / Authority',
};

const CERTAINTY_STYLE: Record<Certainty, string> = {
  measured: 'text-measured border-measured/40',
  estimated: 'text-estimated border-estimated/40',
  unmeasured: 'text-unmeasured border-unmeasured/40',
};

const SEVERITY_DOT: Record<Severity, string> = {
  critical: 'bg-[#E0574A]',
  warning: 'bg-estimated',
  info: 'bg-unmeasured',
};

function scoreColor(score: number): string {
  if (score >= 70) return '#00FFD4';
  if (score >= 40) return '#E0B341';
  return '#E0574A';
}

function CategoryBar({ c }: { c: CategoryScore }): JSX.Element {
  const isGate = c.category === 'crawlability';
  const help = CATEGORY_HELP[c.category];
  return (
    <div className="py-2">
      <div className="flex items-baseline justify-between text-sm mb-1">
        <span className="flex items-center gap-2">
          <span className="text-text-primary">{CATEGORY_LABEL[c.category]}</span>
          <InfoDot>
            <p><span className="text-text-primary">What: </span>{help.what}</p>
            <p><span className="text-text-primary">Why it matters: </span>{help.why}</p>
          </InfoDot>
        </span>
        <span className="font-mono text-text-secondary">
          {!isGate && <span className="mr-3 text-sm">weight {c.weight}%</span>}
          <span style={{ color: scoreColor(c.score) }}>{c.score}</span>
          <span className="text-text-secondary">/100</span>
        </span>
      </div>
      <div className="h-1.5 rounded bg-warm-dark-2 overflow-hidden">
        <div className="h-full rounded" style={{ width: `${c.score}%`, background: scoreColor(c.score) }} />
      </div>
      <div className="mt-1 font-mono text-sm text-text-secondary">
        {Math.round(c.confidence * 100)}% measured
        {c.confidence === 0 && !isGate && ' · fully estimated'}
      </div>
    </div>
  );
}

function FindingRow({ f }: { f: Finding }): JSX.Element {
  const help = findingHelpFor(f.title);
  return (
    <div className="border-t border-warm-dark-2 py-3">
      <div className="flex items-center gap-2 mb-1">
        <span className={`inline-block w-2 h-2 rounded-full ${SEVERITY_DOT[f.severity]}`} />
        <span className="text-sm text-text-primary flex-1 flex items-center gap-2">
          {f.title}
          {help && (
            <InfoDot>
              <p><span className="text-text-primary">What this is: </span>{help.what}</p>
              <p><span className="text-text-primary">How to fix: </span>{help.how}</p>
              {help.tools && help.tools.length > 0 && (
                <p className="flex flex-wrap gap-x-3 gap-y-1">
                  <span className="text-text-primary">Tools:</span>
                  {help.tools.map((t) => (
                    <a key={t.url} href={t.url} target="_blank" rel="noreferrer" className="text-cyan hover:underline">
                      {t.label} ↗
                    </a>
                  ))}
                </p>
              )}
            </InfoDot>
          )}
        </span>
        <span className={`font-mono text-sm uppercase px-1.5 py-0.5 border rounded ${CERTAINTY_STYLE[f.certainty]}`}>
          {f.certainty}
        </span>
      </div>
      <p className="text-sm text-text-secondary ml-4">{f.evidence}</p>
      {/* Free tier gets the diagnosis, not the remedy — backend strips `fix` (#1). */}
      {f.fix ? (
        <p className="text-sm text-cyan/80 ml-4 mt-1">→ {f.fix}</p>
      ) : (
        <p className="text-sm text-text-secondary/70 ml-4 mt-1 italic">→ How to fix this is in the Fix it plan (Pro)</p>
      )}
    </div>
  );
}

export default function Dashboard({ result }: { result: ScanResult }): JSX.Element {
  const { readiness: r } = result;
  return (
    // Categories and findings sit side by side on a wide screen: they are read
    // together (a low category, then which finding caused it) and stacking them
    // pushed the findings a full screen below the bars they explain.
    <div className="w-full max-w-5xl mx-auto mt-6 flex flex-col gap-4">
      {/* Score card */}
      <div className="border border-warm-dark-2 bg-warm-dark-2/20 rounded-xl p-6 flex items-center gap-6">
        <div className="text-center">
          <div className="font-mono text-5xl font-semibold" style={{ color: scoreColor(r.score) }}>
            {r.score}
          </div>
          <div className="font-mono text-sm text-text-secondary">/ 100</div>
        </div>
        <div className="flex-1">
          <div className="font-serif text-xl">AIO Readiness</div>
          <p className="text-sm text-text-secondary mt-1">
            A prediction, not real visibility. It measures how well AI <em>can</em> read and cite you. The
            actual citing is shown by the Visibility probe (direction B).
          </p>
          {r.crawlabilityGate < 1 && (
            <p className="font-mono text-sm text-estimated mt-2">
              ⚠ crawlability gate ×{r.crawlabilityGate}: score is capped
            </p>
          )}
        </div>
      </div>

      {/* Meta / honesty note */}
      <div className="font-mono text-sm text-text-secondary">
        {result.finalUrl}
        {!result.extractionRan && (
          <span className="text-estimated"> · Citability (LLM extraction test) skipped: {
            result.extractionSkippedReason === 'ai-blocked' ? 'site blocks AI crawlers'
              : result.extractionSkippedReason === 'http-error' ? 'page not publicly readable (HTTP error)'
              : result.extractionSkippedReason === 'thin-content' ? 'no readable content'
              : result.extractionSkippedReason === 'cost-cap' ? 'daily budget reached'
              : 'no API key'
          }</span>
        )}
        {result.extractionRan && <span> · via {engineName(result.model)}</span>}
        {result.fromCache && <span className="text-measured"> · cached</span>}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        {/* Category breakdown */}
        <div className="border border-warm-dark-2 bg-warm-dark-2/20 rounded-xl p-5">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-secondary/70 mb-2">Categories</h2>
          {r.categories.map((c) => (
            <CategoryBar key={c.category} c={c} />
          ))}
        </div>

        {/* Findings */}
        <div className="border border-warm-dark-2 bg-warm-dark-2/20 rounded-xl p-5">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.14em] text-text-secondary/70 mb-1">
            Findings ({r.findings.length})
          </h2>
          {r.findings.length === 0 && <p className="text-sm text-text-secondary py-3">None. Clean page.</p>}
          {r.findings.map((f, i) => (
            <FindingRow key={i} f={f} />
          ))}
        </div>
      </div>
    </div>
  );
}
